/**
 * 네이버 맵스 SDK를 로드하는 유틸리티
 */
import { getCurrentLanguage, SupportedLanguage } from '../config/languageConfig';

const NAVER_MAPS_CLIENT_ID = 't9urh3v0l9';
const NAVER_MAPS_TIMEOUT = 10000; // 10초 타임아웃 설정

interface MapLoadStatus {
  isLoaded: boolean;
  isLoading: boolean;
  loadPromise: Promise<boolean> | null;
  currentLanguage: string | null;
  currentScript: HTMLScriptElement | null;
}

// SDK 로드 상태를 추적하는 객체
const mapLoadStatus: MapLoadStatus = {
  isLoaded: false,
  isLoading: false,
  loadPromise: null,
  currentLanguage: null,
  currentScript: null
};

/**
 * 앱 언어를 네이버 지도 API 언어 파라미터로 변환
 * 네이버 지도 API 지원 언어: ko, en, zh, ja
 */
const mapLanguageToNaverMaps = (language: SupportedLanguage): string => {
  switch (language) {
    case 'ko':
      return 'ko';
    case 'en':
      return 'en';
    case 'ja':
      return 'ja';
    case 'zh-CN':
    case 'zh-TW':
      return 'zh';
    default:
      return 'en'; // 기본값은 영어
  }
};

/**
 * 네이버 맵스 SDK가 로드되었는지 확인하는 함수
 */
export const isNaverMapsLoaded = (): boolean => {
  return (
    typeof window.naver !== 'undefined' && 
    window.naver.maps !== undefined && 
    window.naver.maps.Map !== undefined
  );
};

/**
 * 기존 네이버 맵스 리소스 정리
 */
const cleanupNaverMaps = (): void => {
  try {
    // 기존 스크립트 제거
    if (mapLoadStatus.currentScript && mapLoadStatus.currentScript.parentNode) {
      mapLoadStatus.currentScript.parentNode.removeChild(mapLoadStatus.currentScript);
    }
    
    // 다른 네이버 맵스 스크립트들도 제거
    const scripts = document.querySelectorAll('script[src*="openapi.map.naver.com"]');
    scripts.forEach(script => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
    
    // 네이버 객체 정리
    if (typeof window.naver !== 'undefined') {
      delete (window as any).naver;
    }
    
    // 콜백 함수들 정리
    delete (window as any).naverMapLoadCallback;
    delete (window as any).naverMapErrorCallback;
    delete (window as any).naverMapLoaded;
    
    mapLoadStatus.currentScript = null;
    mapLoadStatus.isLoaded = false;
    mapLoadStatus.currentLanguage = null;
  } catch (error) {
    console.warn('네이버 맵스 리소스 정리 중 오류:', error);
  }
};

/**
 * 네이버 맵스 SDK 스크립트를 동적으로 로드하는 함수
 */
const loadNaverMapsScript = (language: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    // 고유한 콜백 함수명 생성
    const callbackName = `naverMapCallback_${Date.now()}`;
    const errorCallbackName = `naverMapErrorCallback_${Date.now()}`;
    
    // 콜백 함수 설정
    (window as any)[callbackName] = () => {
      try {
        mapLoadStatus.isLoaded = true;
        mapLoadStatus.isLoading = false;
        mapLoadStatus.currentLanguage = language;
        
        // 콜백 함수 정리
        delete (window as any)[callbackName];
        delete (window as any)[errorCallbackName];
        
        resolve(true);
      } catch (error) {
        console.error('네이버 맵 로드 콜백 처리 중 오류:', error);
        reject(error);
      }
    };

    (window as any)[errorCallbackName] = (event: any) => {
      console.error('Naver Maps script loading failed:', event);
      mapLoadStatus.isLoading = false;
      mapLoadStatus.loadPromise = null;
      
      // 콜백 함수 정리
      delete (window as any)[callbackName];
      delete (window as any)[errorCallbackName];
      
      reject(new Error('Naver Maps script loading failed'));
    };

    // 스크립트 태그 생성 및 추가
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NAVER_MAPS_CLIENT_ID}&submodules=geocoder&callback=${callbackName}&language=${language}`;
    script.async = true;
    script.onerror = (window as any)[errorCallbackName];
    
    mapLoadStatus.currentScript = script;

    // 타임아웃 설정
    const timeout = setTimeout(() => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      mapLoadStatus.isLoading = false;
      mapLoadStatus.loadPromise = null;
      mapLoadStatus.currentScript = null;
      
      // 콜백 함수 정리
      delete (window as any)[callbackName];
      delete (window as any)[errorCallbackName];
      
      reject(new Error('Naver Maps script loading timed out'));
    }, NAVER_MAPS_TIMEOUT);

    // 로드 성공 시 타임아웃 해제
    script.onload = () => {
      clearTimeout(timeout);
    };

    document.head.appendChild(script);
  });
};

/**
 * 네이버 맵스 SDK 체크 및 로드 함수
 */
export const initNaverMaps = async (): Promise<boolean> => {
  const currentLanguage = mapLanguageToNaverMaps(getCurrentLanguage());
  
  // 이미 같은 언어로 로드되었으면 즉시 완료
  if (isNaverMapsLoaded() && mapLoadStatus.currentLanguage === currentLanguage) {
    return true;
  }

  // 다른 언어로 로드되어 있으면 기존 리소스 정리
  if (mapLoadStatus.currentLanguage && mapLoadStatus.currentLanguage !== currentLanguage) {
    cleanupNaverMaps();
  }

  // 이미 로딩 중이면 기존 프로미스 반환
  if (mapLoadStatus.isLoading && mapLoadStatus.loadPromise) {
    return mapLoadStatus.loadPromise;
  }

  try {
    // 로딩 상태로 변경
    mapLoadStatus.isLoading = true;
    
    // 스크립트 로드
    mapLoadStatus.loadPromise = loadNaverMapsScript(currentLanguage);
    const result = await mapLoadStatus.loadPromise;
    
    return result;
  } catch (error) {
    console.error('Failed to initialize Naver Maps:', error);
    mapLoadStatus.isLoading = false;
    mapLoadStatus.loadPromise = null;
    return false;
  }
}; 